<script setup>
import Sidenav from "@/components/navbar/Sidenav.vue";
import Navbar from "@/components/navbar/Navbar.vue";
import EnrichmentSuccessBanner from "@/components/contacts/EnrichmentSuccessBanner.vue";
import CallBucketPromptBanner from "@/components/contacts/CallBucketPromptBanner.vue";
import TrialBanner from "@/components/billing/TrialBanner.vue";
import BillingBanner from "@/components/billing/BillingBanner.vue";
import SubscriptionInactiveBanner from "@/components/billing/SubscriptionInactiveBanner.vue";
import ImpersonationBanner from "@/components/common/ImpersonationBanner.vue";
import { useAppStore } from "@/composables/app/useAppStore";
import { useCallBucketPrompt } from "@/composables/bucket/useCallBucketPrompt";
import { useSidenavCollapse } from "@/composables/ui/useSidenavCollapse";
import PowerDialerBanner from "@/components/dialer/PowerDialerBanner.vue";
import { usePowerDialer } from "@/composables/dialer";
import { useRoute, useRouter } from "vue-router";
import { DIALER_SESSION_ROUTE } from "@/router/routes";
import { ref, onMounted, onBeforeUnmount } from "vue";

const { enrichmentBanner, hideEnrichmentBanner } = useAppStore();
const { callBucketPrompt, hideCallBucketPrompt, handleCreateBucket } = useCallBucketPrompt();
const { isCollapsed: isSidenavCollapsed, isDrawerMode: isSidenavDrawerMode } = useSidenavCollapse();
const route = useRoute();
const router = useRouter();
// The power dialer runs app-wide (PowerDialerHost in App.vue), but its banner
// is rendered here: it's fixed-positioned against the sidenav and navbar,
// which only exist in this layout — pages without them (onboarding, legal,
// error) just don't show it, while the dialer keeps running regardless.
// The content area is pushed down while it's showing so it isn't covered.
const powerDialer = usePowerDialer();

// The dialer banner is fixed-positioned, so it has to be told where the
// navbar area ends — that's the navbar plus whichever trial/billing/prompt
// banners are currently stacked under it, and changes as they come and go.
// Pinning it at the bare navbar's height covered those banners instead.
const navbarContainerRef = ref(null);
const dialerBannerTop = ref(64);
let navbarResizeObserver = null;

function measureNavbarArea() {
  if (!navbarContainerRef.value) return;
  dialerBannerTop.value = Math.round(navbarContainerRef.value.getBoundingClientRect().bottom);
}

onMounted(() => {
  measureNavbarArea();
  if (typeof ResizeObserver !== "undefined" && navbarContainerRef.value) {
    navbarResizeObserver = new ResizeObserver(measureNavbarArea);
    navbarResizeObserver.observe(navbarContainerRef.value);
  }
  window.addEventListener("resize", measureNavbarArea);
});

onBeforeUnmount(() => {
  navbarResizeObserver?.disconnect();
  window.removeEventListener("resize", measureNavbarArea);
});
</script>

<template>
  <div
    class="default-layout"
    :class="{
      'is-sidenav-collapsed': isSidenavCollapsed || isSidenavDrawerMode,
      'is-sidenav-drawer': isSidenavDrawerMode,
    }"
  >
    <ImpersonationBanner />
    <PowerDialerBanner
      v-if="powerDialer?.hasActiveSession"
      :has-started="powerDialer.hasStarted"
      :session-status="powerDialer.sessionStatus"
      :call-status="powerDialer.callStatus"
      :contact-name="powerDialer.currentContactName"
      :call-duration-formatted="powerDialer.bannerCallDuration"
      :summary="powerDialer.displaySummary"
      :can-trigger-next-call="powerDialer.canTriggerNextCall"
      :awaiting-disposition="!!powerDialer.wrapupTarget"
      :is-requesting-next-call="powerDialer.isSettingPresence"
      :is-pausing="powerDialer.isPausingSession"
      :is-resuming="powerDialer.isResumingSession"
      :is-ending="powerDialer.isEndingSession"
      :show-open-dialer="route.name !== DIALER_SESSION_ROUTE"
      :top-offset="dialerBannerTop"
      @next-call="powerDialer.requestNextCall"
      @pause="powerDialer.pauseSessionHandler"
      @resume="powerDialer.resumeSessionHandler"
      @end="powerDialer.showEndSessionConfirm = true"
      @open-dialer="router.push({ name: DIALER_SESSION_ROUTE })"
    />
    <div class="main-content">
      <Sidenav class="custom-col-width-2" />
      <div class="content-wrapper custom-col-width-10">
        <div ref="navbarContainerRef" class="navbar-container">
          <Navbar />
          <SubscriptionInactiveBanner />
          <BillingBanner />
          <TrialBanner />
          <Transition name="banner-slide">
            <CallBucketPromptBanner
              v-if="callBucketPrompt.visible"
              :call-log-id="callBucketPrompt.callLogId"
              :contact-id="callBucketPrompt.contactId"
              :contact-name="callBucketPrompt.contactName"
              :duration="callBucketPrompt.duration"
              :message="callBucketPrompt.message"
              :timestamp="callBucketPrompt.timestamp"
              @close="hideCallBucketPrompt"
              @create-task="handleCreateBucket"
            />
          </Transition>
          <Transition name="banner-slide">
            <EnrichmentSuccessBanner
              v-if="enrichmentBanner.visible"
              :contacts-count="enrichmentBanner.contactsCount"
              :emails-count="enrichmentBanner.emailsCount"
              :phones-count="enrichmentBanner.phonesCount"
              :bucket-name="enrichmentBanner.bucketName"
              :link-url="enrichmentBanner.linkUrl"
              :contact-ids="enrichmentBanner.contactIds"
              :timestamp="enrichmentBanner.timestamp"
              @close="hideEnrichmentBanner"
              @view-leads="hideEnrichmentBanner"
            />
          </Transition>
        </div>
        <div
          class="content-area"
          :class="{ 'content-area--with-dialer-banner': powerDialer?.hasActiveSession }"
        >
          <slot></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.default-layout {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
  overflow: hidden;
}

.main-content {
  display: flex;
  flex: 1;
  width: 100%;
  min-height: 0;
  overflow: hidden;
  flex-wrap: nowrap;
}

.content-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  height: 100%;
}

.navbar-container {
  flex-shrink: 0;
}

.content-area {
  flex: 1;
  padding: 0;
  max-width: none;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
}

.content-area--with-dialer-banner {
  padding-top: 56px;
}

.content-area::-webkit-scrollbar {
  width: 10px;
}

.content-area::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.content-area::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 5px;
  border: 2px solid #f1f1f1;
}

.content-area::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.banner-slide-enter-active,
.banner-slide-leave-active {
  transition: all 0.3s ease;
  max-height: 80px;
  overflow: hidden;
}

.banner-slide-enter-from,
.banner-slide-leave-to {
  opacity: 0;
  max-height: 0;
  transform: translateY(-10px);
}

.banner-slide-enter-to,
.banner-slide-leave-from {
  opacity: 1;
  max-height: 80px;
  transform: translateY(0);
}
</style>
